import { format } from "date-fns";
import { Field, FieldArray, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ToggleSlider } from "react-toggle-slider";
import { Button, Card, CardBody, Col, Form, Row } from "reactstrap";
import {
  initialValues,
  populateForm,
  testLevels,
  validationSchema,
} from "../../constants/AssessmentsConstants";
import { createAssessment } from "../../store/assessments/action";
import "./QuestionPutTogetherCard.scss";

const QuestionPutTogetherCard = ({ id, onFormSubmit }) => {
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const [assessmentInitialValues, setAssessmentInitialValues] = useState(
    populateForm(initialValues)
  );

  const [currentStep, setCurrentStep] = useState(0);

  const handleNextButton = (event) => {
    event.preventDefault();
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const handlePreviousButton = (event) => {
    event.preventDefault();
    setCurrentStep((prevStep) => Math.max(0, prevStep - 1)); // Prevent going below 0
  };

  const handleSubmit = (values) => {
    const formattedData = {
      assessmentName: values.assessmentName,
      testLevel: values.testLevel,
      expiryDate: format(new Date(values.expiryDate), "yyyy-MM-dd'T'HH:mm:ss"),
      startTime: format(new Date(values.startTime), "yyyy-MM-dd'T'HH:mm:ss"),
      durationHr: values.durationHr,
      durationMin: values.durationMin,
      scoreMin: 0, // Ask Ryan
      testQuestions: values.testQuestions.map((question, index) => ({
        questionId: question.questionId,
        weightage: question.weightage,
        order: index + 1,
      })),
    };
    // Make the toggle work, so set as false for now
    // const randomizedBoolean = values.randomize;
    const randomizedBoolean = false;

    dispatch(
      createAssessment({
        randomizedBoolean: randomizedBoolean,
        data: formattedData,
      })
    );
  };

  // Dropped Card
  const [localDroppedCards, setLocalDroppedCards] = useState([]);
  const [values, setValues] = useState(null);

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "CARD",
    drop: (qnCard) => {
      // make sure no duplicates
      setLocalDroppedCards((prev) => {
        if (prev.some((card) => card.id === qnCard.id)) {
          toast.error("This question has already been added!");
          return prev;
        }

        const newDroppedCards = [...prev, qnCard];

        setValues((prevValues) => {
          const updatedValues = {
            ...prevValues,
            testQuestions: newDroppedCards.map((card) => ({
              questionId: card.id,
              weightage: "0",
            })),
          };
          return updatedValues;
        });
        return newDroppedCards;
      });
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  // When localDroppedCards changes, update formik values
  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        "testQuestions",
        localDroppedCards.map((card) => ({
          questionId: card.id,
          weightage: "0",
        }))
      );
    }
  }, [localDroppedCards]);

  return (
    <div style={{ minHeight: "750px" }}>
      <Formik
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={assessmentInitialValues}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {({ values, errors, touched, setFieldValue }) => {
          return (
            <>
              <Row className="mb-1">
                <Col lg={12}>
                  <div className="d-flex justify-content-end">
                    <Button
                      className="assessment-header-btn btn btn-light"
                      onClick={() => {
                        setAssessmentInitialValues(populateForm(initialValues));
                        setCurrentStep(0);
                      }}
                    >
                      Cancel
                    </Button>
                    {currentStep > 0 ? (
                      <Button
                        className="assessment-header-btn btn btn-light"
                        onClick={handlePreviousButton}
                      >
                        Previous
                      </Button>
                    ) : (
                      <Button
                        className="assessment-header-btn btn btn-success"
                        onClick={handleNextButton}
                      >
                        Next
                      </Button>
                    )}

                    {currentStep === 1 && (
                      <Button
                        className="assessment-header-btn btn btn-success"
                        onClick={() => handleSubmit(values)}
                      >
                        Finish
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
              <Card className="assessment-card">
                <CardBody>
                  <Form>
                    <Row className="mb-3">
                      <Col lg={4}>
                        <div className="d-flex flex-column gap-1">
                          <div>
                            <span>Assessment Name</span>
                            <span className="text-danger">*</span>
                          </div>
                          <Field
                            name="assessmentName"
                            className={`form-control border-light ${
                              errors.assessmentName && touched.assessmentName
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Assessment Name"
                          />
                          {errors.assessmentName && touched.assessmentName && (
                            <div className="invalid-feedback">
                              {errors?.assessmentName}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col lg={4}>
                        <div className="d-flex flex-column gap-1">
                          <div>
                            <span>Role</span>
                            <span className="text-danger">*</span>
                          </div>
                          <Field
                            name="role"
                            className={`form-control border-light ${
                              errors.role && touched.role ? "is-invalid" : ""
                            }`}
                            placeholder="Role"
                          />
                          {errors.role && touched.role && (
                            <div className="invalid-feedback">
                              {errors?.role}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col lg={4}>
                        <div className="d-flex flex-column gap-1">
                          <div>
                            <span>Job Link ID</span>
                          </div>
                          <Field
                            name="jobLinkId"
                            className={`form-select border-light ${
                              errors.jobLinkId && touched.jobLinkId
                                ? "is-invalid"
                                : ""
                            }`}
                            as="select"
                          >
                            <option value="">Select Job ID</option>
                          </Field>
                          {errors.jobLinkId && touched.jobLinkId && (
                            <div className="invalid-feedback">
                              {errors?.jobLinkId}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col lg={12} className="px-1">
                        <Button
                          type="button"
                          className="assessment-header-btn"
                          onClick={() => setCurrentStep(0)}
                        >
                          Assessment Questions
                        </Button>
                        <Button
                          type="button"
                          className="assessment-header-btn"
                          onClick={() => setCurrentStep(1)}
                        >
                          Configuration
                        </Button>
                      </Col>
                    </Row>
                    <hr />
                    <div
                      style={{
                        maxHeight: "400px",
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                    >
                      <Row className="mb-3">
                        <Col lg={12}>
                          {currentStep === 0 && (
                            <div
                              ref={drop}
                              className="drop-qn-area"
                              style={{
                                minHeight: "200px",
                                display: "flex",
                                flexWrap: "wrap",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                border: isOver ? "2px dashed #007bff" : "",
                                borderRadius: "8px",
                                backgroundColor: isOver ? "#FEFEFE" : "#F9FCFF",
                                transition:
                                  "background-color 0.3s, border-color 0.3s",
                                marginTop: "20px",
                              }}
                            >
                              {isOver ? (
                                "Release Questions here"
                              ) : (
                                <div className="my-3">Preview</div>
                              )}
                              {localDroppedCards.length > 0 ? (
                                <FieldArray name={`testQuestions`}>
                                  {({ push, remove }) => (
                                    <>
                                      {localDroppedCards.map(
                                        (questionCard, qIndex) => (
                                          <div
                                            key={qIndex}
                                            className="d-flex flex-column gap-3 w-100 mb-3 p-3"
                                          >
                                            <Row>
                                              <Col lg={10}>
                                                <div className="d-flex flex-row align-items-baseline gap-1">
                                                  <span>{qIndex + 1}.</span>
                                                  <h6>
                                                    {questionCard.questionTitle}
                                                  </h6>
                                                </div>
                                              </Col>
                                              <Col lg={2}>
                                                {/* Hidden Field for Question ID */}
                                                <Field
                                                  type="hidden"
                                                  name={`testQuestions.${qIndex}.questionId`}
                                                />
                                                <input
                                                  type="hidden"
                                                  value={questionCard.id}
                                                  onChange={() =>
                                                    setFieldValue(
                                                      `testQuestions.${qIndex}.questionId`,
                                                      questionCard.id
                                                    )
                                                  }
                                                />

                                                {/* Weightage Input */}
                                                <small>Weightage</small>
                                                <Field
                                                  name={`testQuestions.${qIndex}.weightage`}
                                                  className={`form-control border-light ${
                                                    errors.testQuestions?.[
                                                      qIndex
                                                    ]?.weightage &&
                                                    touched.testQuestions?.[
                                                      qIndex
                                                    ]?.weightage
                                                      ? "is-invalid"
                                                      : ""
                                                  }`}
                                                  placeholder="Weightage"
                                                  style={{ width: "100%" }}
                                                />
                                                {errors.testQuestions?.[qIndex]
                                                  ?.weightage &&
                                                  touched.testQuestions?.[
                                                    qIndex
                                                  ]?.weightage && (
                                                    <div className="invalid-feedback">
                                                      {
                                                        errors?.testQuestions?.[
                                                          qIndex
                                                        ]?.weightage
                                                      }
                                                    </div>
                                                  )}
                                              </Col>
                                            </Row>

                                            {/* Answers Section */}
                                            <Row>
                                              <Col lg={12}>
                                                <div className="fw-semibold">
                                                  Answer(s)
                                                </div>
                                              </Col>
                                            </Row>
                                            <Row>
                                              {questionCard.answer &&
                                              questionCard.answer.length > 0
                                                ? questionCard.answer.map(
                                                    (answer, aIndex) => (
                                                      <Col lg={6} key={aIndex}>
                                                        <div className="d-flex flex-row gap-1 mb-3">
                                                          <span>
                                                            {aIndex + 1}.
                                                          </span>
                                                          <span>
                                                            {answer ||
                                                              "No answer available!"}
                                                          </span>
                                                        </div>
                                                      </Col>
                                                    )
                                                  )
                                                : null}
                                            </Row>

                                            <Row>
                                              <Col lg={12}>
                                                <hr />
                                              </Col>
                                            </Row>
                                          </div>
                                        )
                                      )}
                                    </>
                                  )}
                                </FieldArray>
                              ) : (
                                <p
                                  style={{ color: "#888", fontStyle: "italic" }}
                                >
                                  Drop questions here to add them!
                                </p>
                              )}
                            </div>
                          )}
                          {currentStep === 1 && ( // Configuration
                            <div
                              style={{
                                backgroundColor: "#FAFCFF",
                                paddingRight: "10px",
                              }}
                            >
                              <div className="d-flex flex-column gap-1">
                                <Row className="mb-2">
                                  <Col>
                                    <h6>Configuration</h6>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col lg={6}>
                                    <div className="d-flex flex-row align-items-center gap-2">
                                      <ToggleSlider
                                        handleSize={9}
                                        barHeight={18}
                                        barWidth={35}
                                        barBackgroundColorActive="#13c56b"
                                      />
                                      <span>Random Question Shuffling</span>
                                    </div>
                                  </Col>
                                </Row>
                                <hr />
                                <Row>
                                  <Col>
                                    <div className="d-flex flex-row align-items-center gap-2">
                                      <ToggleSlider
                                        handleSize={9}
                                        barHeight={18}
                                        barWidth={35}
                                        barBackgroundColorActive="#13c56b"
                                      />
                                      <span>Disable Copy/Paste Function</span>
                                    </div>
                                  </Col>
                                </Row>
                                <hr />
                                <Row>
                                  <Col>
                                    <div className="d-flex flex-row align-items-center gap-2">
                                      <ToggleSlider
                                        handleSize={9}
                                        barHeight={18}
                                        barWidth={35}
                                        barBackgroundColorActive="#13c56b"
                                      />
                                      <span>Fullscreen Mode</span>
                                    </div>
                                  </Col>
                                </Row>
                                <hr />
                                <Row>
                                  <Col>
                                    <div className="d-flex flex-row align-items-center gap-2">
                                      <ToggleSlider
                                        handleSize={9}
                                        barHeight={18}
                                        barWidth={35}
                                        barBackgroundColorActive="#13c56b"
                                      />
                                      <span>Enable Screen Recording</span>
                                    </div>
                                  </Col>
                                </Row>
                                <hr />
                                <Row>
                                  <Col>
                                    <h6>Test Difficulty</h6>
                                  </Col>
                                </Row>
                                <Row>
                                  <div className="d-flex align-items-center">
                                    <Col lg={6}>
                                      <div className="d-flex flex-row align-items-center gap-2">
                                        <span>Test Level</span>
                                      </div>
                                    </Col>
                                    <Col lg={6}>
                                      <div className="d-flex justify-content-end">
                                        <Field
                                          name="testLevel"
                                          className={`form-select border-light ${
                                            errors.testLevel &&
                                            touched.testLevel
                                              ? "is-invalid"
                                              : ""
                                          }`}
                                          as="select"
                                          style={{ width: "200px" }}
                                        >
                                          <option value="">
                                            Select Test Level
                                          </option>
                                          {Object.keys(testLevels).map(
                                            (level) => (
                                              <option key={level} value={level}>
                                                {testLevels[level]}
                                              </option>
                                            )
                                          )}
                                        </Field>
                                      </div>
                                      {errors?.testLevel &&
                                        touched?.testLevel && (
                                          <div className="invalid-feedback">
                                            {errors?.testLevel}
                                          </div>
                                        )}
                                    </Col>
                                  </div>
                                </Row>
                                <Row>
                                  <div className="d-flex align-items-center">
                                    <Col lg={6}>
                                      <div className="d-flex flex-row align-items-center gap-2">
                                        <span>Minimum Passing Score</span>
                                      </div>
                                    </Col>
                                    <Col lg={6}>
                                      <div className="d-flex justify-content-end">
                                        <Field
                                          name="minScore"
                                          type="number"
                                          className={`form-control border-light ${
                                            errors.minScore && touched.minScore
                                              ? "is-invalid"
                                              : ""
                                          }`}
                                          style={{ width: "200px" }}
                                          placeholder="Enter Minimum Score"
                                        />
                                      </div>
                                      {errors?.minScore &&
                                        touched?.minScore && (
                                          <div className="invalid-feedback">
                                            {errors?.minScore}
                                          </div>
                                        )}
                                    </Col>
                                  </div>
                                </Row>
                                <hr />
                                <Row>
                                  <Col>
                                    <h6>Assessment Scheduling</h6>
                                  </Col>
                                </Row>
                                <div className="d-flex flex-row align-items-center justify-content-between">
                                  <span>Assessment Duration</span>
                                  <div className="d-flex flex-row align-items-center gap-1">
                                    <Field
                                      name="durationHr"
                                      className={`form-control border-light ${
                                        errors.durationHr && touched.durationHr
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      placeholder="Hour"
                                      style={{ width: "100px" }}
                                    />
                                    <Field
                                      name="durationMin"
                                      className={`form-control border-light ${
                                        errors.durationMin &&
                                        touched.durationMin
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      placeholder="Min"
                                      style={{ width: "100px" }}
                                    />
                                  </div>
                                </div>
                                <div className="d-flex flex-row align-items-center justify-content-between">
                                  <span>Assessment Start Date</span>
                                  <div>
                                    <Field
                                      name="startTime"
                                      type="datetime-local"
                                      className={`form-control border-light`}
                                      style={{ width: "200px" }}
                                    />
                                  </div>
                                </div>
                                <div className="d-flex flex-row align-items-center justify-content-between">
                                  <span>Assessment Expiry Date</span>
                                  <div>
                                    <Field
                                      name="expiryDate"
                                      type="datetime-local"
                                      className={`form-control border-light`}
                                      style={{ width: "200px" }}
                                    />
                                  </div>
                                </div>
                                <hr />
                              </div>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default QuestionPutTogetherCard;
